import { z } from 'zod';

import { createTranslationSchema } from './translation';
import { allowVariablePlaceholders } from '$lib/config-schemas/raw-variable-placeholder';

export const GeometryTypes = {
  BOX: 'box',
  SPHERE: 'sphere',
  CAPSULE: 'capsule',
} as const;

const createBoxSchema = (allowVariables = false) => {
  // When variables are allowed, don't apply defaults as they interfere with variable placeholders
  if (allowVariables) {
    return z.object({
      type: z.literal(GeometryTypes.BOX),
      x: z.coerce.number().nonnegative(),
      y: z.coerce.number().nonnegative(),
      z: z.coerce.number().nonnegative(),
      translation: allowVariablePlaceholders(
        createTranslationSchema(allowVariables),
        allowVariables
      ).optional(),
    });
  }
  return z.object({
    type: z.literal(GeometryTypes.BOX).default(GeometryTypes.BOX),
    x: z.coerce.number().nonnegative().default(0),
    y: z.coerce.number().nonnegative().default(0),
    z: z.coerce.number().nonnegative().default(0),
    translation: createTranslationSchema(allowVariables).optional(),
  });
};

export const BoxSchema = createBoxSchema();

export type BoxGeometry = z.infer<typeof BoxSchema>;

const createSphereSchema = (allowVariables = false) => {
  // When variables are allowed, don't apply defaults as they interfere with variable placeholders
  if (allowVariables) {
    return z.object({
      type: z.literal(GeometryTypes.SPHERE),
      r: z.coerce.number().nonnegative(),
      translation: allowVariablePlaceholders(
        createTranslationSchema(allowVariables),
        allowVariables
      ).optional(),
    });
  }
  return z.object({
    type: z.literal(GeometryTypes.SPHERE).default(GeometryTypes.SPHERE),
    r: z.coerce.number().nonnegative().default(0),
    translation: createTranslationSchema(allowVariables).optional(),
  });
};

export const SphereSchema = createSphereSchema();

export type SphereGeometry = z.infer<typeof SphereSchema>;

const createCapsuleSchema = (allowVariables = false) => {
  // When variables are allowed, don't apply defaults as they interfere with variable placeholders
  if (allowVariables) {
    return z.object({
      type: z.literal(GeometryTypes.CAPSULE),
      r: z.coerce.number().nonnegative(),
      l: z.coerce.number().nonnegative(),
      translation: allowVariablePlaceholders(
        createTranslationSchema(allowVariables),
        allowVariables
      ).optional(),
    });
  }
  return z.object({
    type: z.literal(GeometryTypes.CAPSULE).default(GeometryTypes.CAPSULE),
    r: z.coerce.number().nonnegative().default(0),
    l: z.coerce.number().nonnegative().default(0),
    translation: createTranslationSchema(allowVariables).optional(),
  });
};

export const CapsuleSchema = createCapsuleSchema();

export type CapsuleGeometry = z.infer<typeof CapsuleSchema>;

export const GeometrySchema = z.union([BoxSchema, CapsuleSchema, SphereSchema]);

export type Geometry = z.infer<typeof GeometrySchema>;
export type GeometryType = Geometry['type'];

export const createGeometrySchema = (allowVariables = false) => {
  return z.union([
    allowVariablePlaceholders(createBoxSchema(allowVariables), allowVariables),
    allowVariablePlaceholders(
      createCapsuleSchema(allowVariables),
      allowVariables
    ),
    allowVariablePlaceholders(
      createSphereSchema(allowVariables),
      allowVariables
    ),
  ]);
};
